import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

/**
 * PhoneMockup — reusable iPhone-style device frame with a screen slot.
 * Renders at whatever pixel width you give it; height auto (aspect 9:19.5).
 *
 * Props:
 *   width: number (default 360)
 *   glow: boolean (soft emerald glow behind the device) — default true
 *   float: boolean (subtle y-float animation) — default true
 *   className: string
 *   children: content rendered inside the phone screen
 */
export const PhoneMockup = ({
  width = 360,
  glow = true,
  float = true,
  className,
  children,
  "data-testid": testId = "phone-mockup",
}) => {
  const reduce = useReducedMotion();
  const height = Math.round(width * (812 / 375));

  return (
    <div
      className={cn("relative", className)}
      style={{ width, height }}
      data-testid={testId}
    >
      {glow && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 blur-3xl"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(0,230,118,0.30) 0%, rgba(0,0,0,0) 65%)",
            transform: "scale(1.15)",
          }}
        />
      )}

      <motion.div
        className="relative h-full w-full"
        animate={
          !reduce && float
            ? { y: [0, -8, 0] }
            : { y: 0 }
        }
        transition={{
          duration: 6.5,
          ease: "easeInOut",
          repeat: Infinity,
        }}
      >
        {/* Outer bezel */}
        <div
          className="absolute inset-0 rounded-[52px] p-[3px]"
          style={{
            background:
              "linear-gradient(155deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.04) 40%, rgba(255,255,255,0.02) 70%, rgba(0,230,118,0.12) 100%)",
            boxShadow:
              "0 40px 80px -30px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.05)",
          }}
        >
          {/* Inner body */}
          <div
            className="relative h-full w-full overflow-hidden rounded-[50px]"
            style={{
              background: "linear-gradient(180deg, #0a0a0a 0%, #050505 100%)",
            }}
          >
            {/* Dynamic island */}
            <div
              aria-hidden="true"
              className="absolute left-1/2 top-3 z-30 h-7 w-[110px] -translate-x-1/2 rounded-full bg-black"
              style={{
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)",
              }}
            >
              <span className="absolute right-3 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-brand-emerald/70 shadow-[0_0_8px_rgba(0,230,118,0.7)]" />
            </div>

            {/* Screen surface — 3px inset from body edge */}
            <div className="absolute inset-[3px] overflow-hidden rounded-[47px] bg-[#0b0b0b]">
              {children}
            </div>
          </div>
        </div>

        {/* Side buttons */}
        <div className="absolute -left-[3px] top-[110px] h-8 w-[3px] rounded-r bg-white/[0.06]" />
        <div className="absolute -left-[3px] top-[160px] h-14 w-[3px] rounded-r bg-white/[0.06]" />
        <div className="absolute -left-[3px] top-[230px] h-14 w-[3px] rounded-r bg-white/[0.06]" />
        <div className="absolute -right-[3px] top-[190px] h-20 w-[3px] rounded-l bg-white/[0.06]" />
      </motion.div>
    </div>
  );
};

export default PhoneMockup;
